import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import getCaretCoordinates from 'textarea-caret';

import { AtMentionMenuComponent } from './at-mention-menu/at-mention-menu.component';
import { AtMentionItem, AtMentionItemType } from './at-mention.model';

export interface ChoiceSelectedEvent {
  choice :any;
  insertedAt :{
    start :number;
    end :number;
  };
}

@Directive({
  selector: 'textarea[atMention],input[type="text"][atMention]',
})
export class AtMentionDirective implements OnDestroy {
  /**
   * The character that will trigger the menu to appear
   */
  @Input() triggerCharacter = '@';

  /**
   * The regular expression that will match the search text after the trigger character
   */
  @Input() searchRegexp = /^\w*$/;

  /**
   * The menu component to show with available options.
   * You can extend the built in `TextInputAutocompleteMenuComponent` component to use a custom template
   */
  @Input() menuComponent = AtMentionMenuComponent;

  /**
   * Called when the options menu is shown
   */
  @Output() menuShown = new EventEmitter();

  /**
   * Called when the options menu is hidden
   */
  @Output() menuHidden = new EventEmitter();

  /**
   * Called when a choice is selected
   */
  @Output() choiceSelected = new EventEmitter<ChoiceSelectedEvent>();

  @Input() atMentionItems :AtMentionItem[];

  @Input() commands = {
    topic: false,
    project: false,
  };

  /* tslint:disable member-ordering */
  private menu :| {
        component :ComponentRef<AtMentionMenuComponent>;
        triggerCharacterPosition :number;
        lastCaretPosition ?:number;
      }
    | undefined;

  private menuHidden$ = new Subject();

  constructor(
    private componentFactoryResolver :ComponentFactoryResolver,
    private viewContainerRef :ViewContainerRef,
    private injector :Injector,
    private elm :ElementRef,
    private appRef :ApplicationRef,
  ) {}

  @HostListener('document:keydown.Escape')
  onEscape() {
    if (this.menu) {
      this.hideMenu();
    }
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.menu) {
      this.hideMenu();
    }
  }

  @HostListener('input', ['$event'])
  onChange(event :any) {
    if (event.data === this.triggerCharacter) {
      this.showMenu();
    }
    if (this.menu) {
      const cursor = this.elm.nativeElement.selectionStart;
      if (cursor < this.menu.triggerCharacterPosition) {
        this.hideMenu();
      } else {
        const searchText = event.target.value.slice(
          this.menu.triggerCharacterPosition,
          cursor,
        );
        if (!searchText.match(this.searchRegexp)) {
          this.hideMenu();
        } else {
          this.updateMenuWithChoices(searchText);
        }
      }
    }
  }

  appendComponentToBody(component :any) {
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(component)
      .create(this.injector);
    this.appRef.attachView(componentRef.hostView);
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    return componentRef as ComponentRef<AtMentionMenuComponent>;
  }
  removeComponentFromBody(componentRef :ComponentRef<any>) {
    if (componentRef && componentRef.hostView) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
  }

  private getChoiceLabel(choice :AtMentionItem) {
    if (choice.type === AtMentionItemType.USER) {
      return `${choice.name} `;
    } else if (choice.type === AtMentionItemType.COMMAND) {
      return `${choice.id} `;
    }
  }

  private addCommandsToChoices() {
    const commandsToShow = [];
    Object.keys(this.commands).forEach((key) => {
      if (this.commands[key]) {
        commandsToShow.push({
          id: key,
          name: key,
          type: AtMentionItemType.COMMAND,
        });
      }
    });
    if (commandsToShow.length > 0) {
      commandsToShow.push({
        id: 0,
        name: '',
        type: AtMentionItemType.DIVIDER,
      });
    }

    return commandsToShow;
  }

  private updateMenuWithChoices(searchText :string) {
    this.menu.component.instance.searchText = searchText;
    this.menu.component.instance.choices = [];
    this.menu.component.instance.choiceLoadError = undefined;
    this.menu.component.instance.choiceLoading = true;
    this.menu.component.changeDetectorRef.detectChanges();
    const choices = this.getMatchingItems(searchText, this.atMentionItems);
    const commands = this.addCommandsToChoices();
    if (this.menu) {
      this.menu.component.instance.choices = [...commands, ...choices];
      this.menu.component.instance.choiceLoading = false;
      this.menu.component.changeDetectorRef.detectChanges();
    }
  }

  @HostListener('blur')
  onBlur() {
    if (this.menu) {
      this.menu.lastCaretPosition = this.elm.nativeElement.selectionStart;
    }
  }

  private applyChoice(choice :AtMentionItem) {
    const label = this.getChoiceLabel(choice);
    const textarea :HTMLTextAreaElement = this.elm.nativeElement;
    const value :string = textarea.value;
    const startIndex = this.menu!.triggerCharacterPosition;
    const start = value.slice(0, startIndex);
    const caretPosition = this.menu!.lastCaretPosition || textarea.selectionStart;
    const end = value.slice(caretPosition);
    textarea.value = start + label + end;
    // force ng model / form control to update
    textarea.dispatchEvent(new Event('input'));
    this.hideMenu();
    const setCursorAt = (start + label).length;
    textarea.setSelectionRange(setCursorAt, setCursorAt);
    textarea.focus();
    this.choiceSelected.emit({
      choice,
      insertedAt: {
        start: startIndex,
        end: startIndex + label.length,
      },
    });
  }
  private showMenu() {
    if (!this.menu) {
      const menuFactory =
        this.componentFactoryResolver.resolveComponentFactory<AtMentionMenuComponent>(
          this.menuComponent,
        );
      this.menu = {
        component: this.appendComponentToBody(this.menuComponent),
        triggerCharacterPosition: this.elm.nativeElement.selectionStart,
      };

      const lineHeight = +getComputedStyle(
        this.elm.nativeElement,
      ).lineHeight!.replace(/px$/, '');

      const rect = this.elm.nativeElement.getBoundingClientRect();
      const { top, left } = getCaretCoordinates(
        this.elm.nativeElement,
        this.elm.nativeElement.selectionStart,
      );
      this.menu.component.instance.position = {
        top: top + lineHeight + rect.top,
        left: left + rect.left,
      };
      this.menu.component.changeDetectorRef.detectChanges();
      this.menu.component.instance.selectChoice
        .pipe(takeUntil(this.menuHidden$))
        .subscribe((choice :AtMentionItem) => {
          this.applyChoice(choice);
        });
      this.menuShown.emit();
    }
  }

  private hideMenu() {
    if (this.menu) {
      this.removeComponentFromBody(this.menu.component);
      this.menuHidden$.next(true);
      this.menuHidden.emit();
      this.menu = undefined;
    }
  }

  private getMatchingItems(searchText :string, items :AtMentionItem[]) {
    if (items && items.length > 0) {
      return items.filter((item) =>
        item.name.toLowerCase().includes(searchText.toLowerCase()),
      );
    }
  }

  ngOnDestroy() {
    this.hideMenu();
  }
}
